import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Rocket, CircleDot, ArrowUp, ArrowDown, Timer, CheckCircle2,
  Zap, Shield, BarChart3, Lock, Cpu, TrendingUp, Brain, Activity,
} from "lucide-react";

const STEPS = [
  { num: "01", icon: BarChart3, title: "Pick a market", desc: "Choose BTC, ETH, or another supported asset." },
  { num: "02", icon: ArrowUp, title: "Choose up or down", desc: "Predict the price direction for the next round." },
  { num: "03", icon: CircleDot, title: "Enter your position", desc: "Set your stake. Funds stay in a non-custodial vault." },
  { num: "04", icon: CheckCircle2, title: "Settle in seconds", desc: "Round resolves in 15–30 seconds. Winners share the pool." },
];

const BENEFITS = [
  { icon: Timer, title: "Fast rounds", desc: "15–30 second cycles. No waiting hours for resolution." },
  { icon: BarChart3, title: "Clear price action", desc: "Simple up/down on real crypto prices, not complex derivatives." },
  { icon: Zap, title: "AI signal support", desc: "Optional AI-assisted signals help you read short-term momentum." },
  { icon: Lock, title: "No custody risk", desc: "Your funds stay in on-chain vaults. Non-custodial by design." },
];

const POWERS = [
  {
    icon: Cpu,
    label: "Internal benchmark",
    title: "Speed-focused execution",
    desc: "Sub-100ms off-chain engine for near-instant trade matching — internal benchmark.",
  },
  {
    icon: Zap,
    label: "Advisory only",
    title: "AI-assisted signals",
    desc: "Real-time order book sentiment analysis — advisory signals only, not trading advice.",
  },
  {
    icon: Shield,
    label: "Verifiable",
    title: "Transparent settlement",
    desc: "On-chain proof and settlement on Base Chain. Round results are verifiable.",
  },
  {
    icon: TrendingUp,
    label: "No emission incentives",
    title: "Revenue-backed model",
    desc: "100% of trading fees routed to $QVX buyback and burn — no emission-driven incentives.",
  },
];

const AI_SIGNALS = [
  { icon: Activity, title: "Momentum", desc: "Short-term price direction analysis based on recent market movement." },
  { icon: BarChart3, title: "Order Book Depth", desc: "Volume imbalance signal across bid/ask levels." },
  { icon: Zap, title: "Volatility Index", desc: "Market regime detection for current price uncertainty." },
  { icon: Brain, title: "Confidence Score", desc: "Signal reliability estimate for each market and round." },
];

const TRUST = [
  {
    title: "Non-custodial settlement",
    desc: "Funds settle through Base smart contracts. You retain control at all times.",
  },
  {
    title: "On-chain proof layer",
    desc: "Round results are tied to verifiable on-chain commitments. Outcomes are transparent.",
  },
  {
    title: "AI is advisory only",
    desc: "Signals support decision-making but do not guarantee outcomes. All trading carries risk.",
  },
  {
    title: "Multi-source price aggregation",
    desc: "Designed to reduce dependency on any single price feed.",
  },
];

const TOKENOMICS_FLOW = [
  { label: "Every Round Fee", sublabel: "2% protocol fee" },
  { label: "Buyback Engine", sublabel: "Automated routing" },
  { label: "Buy $QVX", sublabel: "Open market" },
  { label: "Burn", sublabel: "Permanently removed" },
  { label: "Transparent Events", sublabel: "On-chain log" },
];

const BETA_CARDS = [
  { icon: Timer, title: "Test fast rounds", desc: "Experience 15–30 second prediction rounds in a controlled testnet environment." },
  { icon: Brain, title: "Try AI signals", desc: "Access AI-assisted market signals before the public launch." },
  { icon: Zap, title: "Shape the protocol", desc: "Give direct feedback that influences the final protocol design." },
  { icon: TrendingUp, title: "Support on Surge", desc: "Help Qovarix reach Surge Ignition and support the launch journey." },
];

function TelemetryCard({ label, value, delta, side }) {
  return (
    <div className={`telemetry-card telemetry-${side}`}>
      <div className="glass-panel px-4 py-3 inline-flex flex-col gap-1">
        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">{label}</span>
        <span className="font-mono text-base font-semibold text-white">{value}</span>
        <span className="font-mono text-xs text-[#B5FF00]">{delta}</span>
      </div>
    </div>
  );
}

export default function ScrollOverlay({ onLaunch, onLaunchApp }) {
  const [roundId, setRoundId] = useState(4291);
  const [secondsRemaining, setSecondsRemaining] = useState(30);
  const [poolUp, setPoolUp] = useState(58);

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          setRoundId((r) => r + 1);
          setPoolUp(45 + Math.floor(Math.random() * 20));
          return 30;
        }
        return prev - 1;
      });

      setPoolUp((prev) => {
        const drift = Math.floor(Math.random() * 7) - 3;
        return Math.max(18, Math.min(82, prev + drift));
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const poolDown = 100 - poolUp;
  const countdown = `00:${String(secondsRemaining).padStart(2, "0")}`;
  const isBullish = poolUp >= 50;
  const signalTrend = isBullish ? "Bullish" : "Bearish";
  const imbalance = Math.abs(poolUp - 50);
  const signalConfidence = Math.min(82, Math.max(58, 58 + Math.round(imbalance * 1.2)));

  return (
    <div data-testid="scroll-overlay">

      {/* ═══════ HERO ═══════ */}
      <section data-testid="hero-section" className="min-h-screen flex flex-col items-center justify-start relative px-6 pt-28 sm:pt-32">
        <motion.div
          className="text-center max-w-3xl relative z-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.3 }}
        >
          {/* Status badge */}
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-[#00F0FF]/20 bg-[#00F0FF]/5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#B5FF00] animate-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
              Closed Beta · Testnet preparing for launch on Base Chain
            </span>
          </div>

          <h1 className="font-grotesk text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter text-white leading-none mb-5 text-glow-cyan">
            AI-powered 15–30s<br />prediction markets on Base.
          </h1>

          <p className="font-grotesk text-base lg:text-lg text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            Qovarix combines a low-latency execution engine, real-time AI market signals, and non-custodial
            on-chain settlement for ultra-short crypto prediction rounds.
          </p>

          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              data-testid="hero-launch-btn"
              onClick={onLaunch}
              className="hero-launch-btn inline-flex items-center gap-3"
            >
              <Rocket size={16} strokeWidth={1.5} /><span>Join Closed Beta</span>
            </button>
            <button
              data-testid="hero-launch-app-btn"
              onClick={onLaunchApp}
              className="hero-launch-btn cyan-glow inline-flex items-center gap-2"
            >
              <Rocket size={13} strokeWidth={1.5} /> Launch
            </button>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 animate-pulse-glow">Scroll to explore</span>
        </motion.div>

        <motion.div
          className="absolute bottom-28 left-6 lg:left-14 hidden lg:block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <TelemetryCard label="Execution Speed" value="87ms" delta="Internal benchmark" side="left" />
        </motion.div>

        <motion.div
          className="absolute bottom-40 right-6 lg:right-14 hidden lg:block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
        >
          <TelemetryCard label="Round Duration" value="15–30s" delta="Target metric" side="right" />
        </motion.div>
      </section>

      {/* ═══════ PRODUCT PREVIEW ═══════ */}
      <section data-testid="product-preview" className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline mb-3 block text-center">// LIVE ROUND PREVIEW</span>
            <h2 className="font-grotesk text-2xl sm:text-3xl font-bold tracking-tight text-white text-center mb-10">
              What a Qovarix round looks like.
            </h2>

            <div className="glass-panel-active p-6 sm:p-8 max-w-md mx-auto">
              {/* Terminal header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="font-mono text-sm font-semibold text-white">BTC/USD</span>
                  <span className="font-mono text-[10px] text-slate-500 ml-2">· ROUND #{roundId}</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-[#00F0FF]/20 bg-[#00F0FF]/5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF]" />
                  <span className="font-mono text-[9px] uppercase tracking-wider text-[#00F0FF]">Base Verified</span>
                </div>
              </div>

              {/* Countdown */}
              <div className="text-center mb-6">
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Round closes in</p>
                <span className="font-mono text-5xl font-bold text-white tabular-nums">{countdown}</span>
              </div>

              {/* AI Signal row */}
              <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-[#B5FF00]/20 bg-[#B5FF00]/5 mb-5">
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">AI Signal</span>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <span className={`font-mono text-sm font-semibold ${isBullish ? "text-[#B5FF00]" : "text-red-400"}`}>
                    {signalTrend} {signalConfidence}%
                  </span>
                  <span className="font-mono text-[10px] text-slate-500">· Medium Risk</span>
                  <span className="font-mono text-[9px] text-slate-600">Advisory only</span>
                </div>
              </div>

              {/* UP / DOWN buttons (visual only) */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="flex items-center justify-center gap-2 py-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 cursor-default select-none">
                  <ArrowUp size={15} className="text-emerald-400" />
                  <span className="font-mono text-sm font-semibold text-emerald-400">UP</span>
                </div>
                <div className="flex items-center justify-center gap-2 py-3 rounded-lg border border-red-500/30 bg-red-500/10 cursor-default select-none">
                  <ArrowDown size={15} className="text-red-400" />
                  <span className="font-mono text-sm font-semibold text-red-400">DOWN</span>
                </div>
              </div>

              {/* Pool split */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Pool split</span>
                </div>
                <div className="flex rounded-full overflow-hidden h-2 mb-2">
                  <div className="bg-emerald-500/60 transition-all duration-700" style={{ width: `${poolUp}%` }} />
                  <div className="bg-red-500/40 flex-1" />
                </div>
                <div className="flex justify-between">
                  <span className="font-mono text-[10px] text-emerald-400">UP {poolUp}%</span>
                  <span className="font-mono text-[10px] text-red-400">DOWN {poolDown}%</span>
                </div>
              </div>
            </div>

            <p className="font-mono text-[10px] text-slate-600 text-center mt-4">
              Simulated live preview for illustration purposes. UI subject to change before public launch.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ HOW IT WORKS ═══════ */}
      <section data-testid="how-it-works" className="min-h-screen flex items-center relative py-20 px-6">
        <div className="max-w-5xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline mb-3 block text-center">// HOW IT WORKS</span>
            <h2 className="font-grotesk text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white text-center mb-12">
              Four steps. Seconds to settle.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.num}
                    className="glass-panel-active p-6 relative"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                  >
                    <span className="font-mono text-[10px] text-[#00F0FF]/60">{s.num}</span>
                    <Icon size={20} className="text-[#00F0FF] mt-3 mb-3" strokeWidth={1.5} />
                    <h3 className="font-grotesk text-lg font-semibold text-white mb-2">{s.title}</h3>
                    <p className="font-grotesk text-sm text-slate-400">{s.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ WHY QOVARIX ═══════ */}
      <section data-testid="why-traders" className="min-h-screen flex items-center relative py-20 px-6">
        <div className="max-w-5xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline mb-3 block text-center">// WHY QOVARIX</span>
            <h2 className="font-grotesk text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white text-center mb-12">
              Built for fast decision-making.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {BENEFITS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <motion.div
                    key={b.title}
                    className="glass-panel p-6 flex items-start gap-4"
                    initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: false }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                  >
                    <Icon size={18} className="text-[#00F0FF] mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <h3 className="font-grotesk text-base font-semibold text-white mb-1">{b.title}</h3>
                      <p className="font-grotesk text-sm text-slate-400">{b.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ UNDER THE HOOD ═══════ */}
      <section data-testid="powers-engine" className="min-h-screen flex items-center relative py-20 px-6">
        <div className="max-w-5xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline mb-3 block text-center">// UNDER THE HOOD</span>
            <h2 className="font-grotesk text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white text-center mb-12">
              What powers the engine.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {POWERS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div
                    key={p.title}
                    className="glass-panel p-6 flex items-start gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                  >
                    <Icon size={18} className="text-[#B5FF00] mt-1 shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-grotesk text-base font-semibold text-white">{p.title}</h3>
                        <span className="font-mono text-[9px] uppercase tracking-wider text-slate-600 border border-slate-700/60 px-1.5 py-0.5 rounded">
                          {p.label}
                        </span>
                      </div>
                      <p className="font-grotesk text-sm text-slate-400">{p.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ AI SIGNAL LAYER ═══════ */}
      <section data-testid="ai-signal-section" className="relative py-20 px-6">
        <div className="max-w-5xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline mb-3 block text-center">// AI SIGNAL LAYER</span>
            <h2 className="font-grotesk text-3xl sm:text-4xl font-bold tracking-tight text-white text-center mb-3">
              Real-time market intelligence.
            </h2>
            <p className="font-grotesk text-sm text-slate-400 text-center max-w-xl mx-auto mb-10">
              Four signal types feed into the Qovarix AI engine to help participants read short-term market conditions.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {AI_SIGNALS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.title}
                    className="glass-panel-active p-6"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                  >
                    <Icon size={20} className="text-[#B5FF00] mb-3" strokeWidth={1.5} />
                    <h3 className="font-grotesk text-base font-semibold text-white mb-2">{s.title}</h3>
                    <p className="font-grotesk text-sm text-slate-400">{s.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            <div className="glass-panel px-6 py-4 max-w-3xl mx-auto border-l-2 border-[#B5FF00]/30">
              <p className="font-grotesk text-xs text-slate-500 leading-relaxed">
                <span className="text-[#B5FF00] font-semibold">Advisory disclaimer: </span>
                AI signals are advisory only and do not guarantee profitable outcomes. Past signal performance
                is not indicative of future results. All trading involves substantial risk.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ SETTLEMENT & TRUST ═══════ */}
      <section data-testid="trust-section" className="min-h-screen flex items-center relative py-20 px-6">
        <div className="max-w-4xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline mb-3 block text-center">// SETTLEMENT & TRUST</span>
            <h2 className="font-grotesk text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white text-center mb-12">
              Built for transparent execution.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TRUST.map((t, i) => (
                <motion.div
                  key={t.title}
                  className="glass-panel-active p-6"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: false }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div className="w-2 h-2 rounded-full bg-[#00F0FF] mb-4" />
                  <h3 className="font-grotesk text-lg font-semibold text-white mb-2">{t.title}</h3>
                  <p className="font-grotesk text-sm text-slate-400">{t.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ $QVX TOKENOMICS ═══════ */}
      <section data-testid="tokenomics-section" className="relative py-20 px-6">
        <div className="max-w-5xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline mb-3 block text-center">// $QVX TOKENOMICS</span>
            <h2 className="font-grotesk text-3xl sm:text-4xl font-bold tracking-tight text-white text-center mb-3">
              Revenue-backed token design.
            </h2>
            <p className="font-grotesk text-sm text-slate-400 text-center max-w-xl mx-auto mb-10">
              Qovarix is designed around revenue-backed tokenomics. 100% of protocol trading fees are routed
              toward $QVX buyback and burn, avoiding long-term emission-driven incentives.
            </p>

            {/* Fee flow diagram */}
            <div className="glass-panel p-6 sm:p-8 mb-6">
              <div className="flex items-stretch justify-center gap-0 flex-wrap">
                {TOKENOMICS_FLOW.map((step, i) => (
                  <div key={step.label} className="flex items-center">
                    <div className="flex flex-col items-center text-center px-4 py-3">
                      <span className="font-mono text-xs font-semibold text-white whitespace-nowrap">{step.label}</span>
                      <span className="font-mono text-[9px] text-slate-500 mt-1 whitespace-nowrap">{step.sublabel}</span>
                    </div>
                    {i < TOKENOMICS_FLOW.length - 1 && (
                      <span className="font-mono text-[#00F0FF] text-xl mx-1 shrink-0 self-center">→</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-panel px-6 py-4 max-w-3xl mx-auto border-l-2 border-slate-700">
              <p className="font-grotesk text-xs text-slate-500 leading-relaxed">
                <span className="text-slate-400 font-semibold">Disclaimer: </span>
                Tokenomics described reflect the intended protocol design. Token value and price are subject
                to market conditions and are not guaranteed.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ CLOSED BETA ACCESS ═══════ */}
      <section data-testid="closed-beta-section" className="relative py-20 px-6">
        <div className="max-w-5xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline mb-3 block text-center">// CLOSED BETA</span>
            <h2 className="font-grotesk text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white text-center mb-3">
              Join the Qovarix Closed Beta.
            </h2>
            <p className="font-grotesk text-sm text-slate-400 text-center max-w-xl mx-auto mb-10">
              We are onboarding an early cohort of testers and traders before public launch. Help shape the
              protocol and get first access to the Qovarix testnet.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {BETA_CARDS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <motion.div
                    key={c.title}
                    className="glass-panel-active p-6"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                  >
                    <Icon size={20} className="text-[#00F0FF] mb-3" strokeWidth={1.5} />
                    <h3 className="font-grotesk text-base font-semibold text-white mb-2">{c.title}</h3>
                    <p className="font-grotesk text-sm text-slate-400">{c.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            <div className="flex items-center justify-center gap-4 flex-wrap">
              <button
                onClick={onLaunch}
                className="hero-launch-btn inline-flex items-center gap-3"
              >
                <Rocket size={16} strokeWidth={1.5} /> Request Beta Access
              </button>
              <a
                href="https://surge.gg"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-xs uppercase tracking-[0.18em] text-slate-300 hover:text-[#B5FF00] transition-colors px-5 py-4 border border-white/10 hover:border-[#B5FF00]/30 inline-flex items-center gap-2"
              >
                Ignite on Surge ↗
              </a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
